import { forwardRef } from "react";

import { Box, Stack, SxProps, Tab, TabProps, Theme, useTheme } from "@mui/material";

import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon";
import { FormTooltip } from "@/forms-design-system/FormTooltip";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { IconElement } from "@/icons/types";

export type FormTabProps = Omit<TabProps, "icon" | "disableRipple"> & {
  icon?: IconElement;
  size?: "small" | "medium";
  tooltip?: React.ReactNode;
  backgroundContent?: React.ReactNode;
  components?: {
    icon?: {
      sx?: SxProps<Theme>;
      size?: FormIconProps["size"];
      color?: FormIconProps["color"];
    };
    label?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const FormTab = forwardRef<HTMLDivElement, FormTabProps>(
  ({ icon, size = "medium", label, tooltip, components, sx, backgroundContent, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.tabs) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        minWidth: 0,
        maxWidth: "none", // Override MUI default style
        minHeight: 0, // Override MUI default style
        backgroundColor: theme.palette.formsComponents.tabs.tabBackgroundColor,
        borderRadius: theme.spacing(1),
        color: theme.palette.formsComponents.tabs.tabTextColor,
        ...(tooltip && {
          width: "100%",
        }),

        textTransform: "none", // Override MUI default style
        opacity: 1, // Override MUI default style
        transition: "background-color 200ms ease-out, color 200ms ease-out, outline 100ms ease-out",
        ...(size === "small" && {
          height: "32px",
          padding: theme.spacing(0.5, 1),
          ...theme.typography.subtitle2,
        }),
        ...(size === "medium" && {
          height: "44px",
          padding: theme.spacing(1, 2),
          ...theme.typography.subtitle1,
          lineHeight: "26px",
        }),
        ["@media (max-width: 335px)"]: {
          // Reduce inline padding for live preview only
          paddingInline: 0.5,
        },
        ["&:hover"]: {
          backgroundColor: theme.palette.formsComponents.tabs.tabHoverBackgroundColor,
          color: theme.palette.formsComponents.tabs.tabTextColor,
        },
        ["&:active"]: {
          backgroundColor: theme.palette.formsComponents.tabs.tabHoverBackgroundColor,
          color: theme.palette.formsComponents.tabs.tabTextColor,
        },
        ["&.Mui-selected"]: {
          backgroundColor: theme.palette.formsComponents.tabs.tabSelectedBackgroundColor,
          color: theme.palette.formsComponents.tabs.tabSelectedTextColor,
        },
        ["&:focus-visible"]: {
          backgroundColor: theme.palette.formsComponents.tabs.tabHoverBackgroundColor,
          color: theme.palette.formsComponents.tabs.tabTextColor,
        },
        ["&.Mui-disabled"]: {
          opacity: 0.4,
        },
      },
      sx
    );

    const renderLabel = () => {
      return (
        <Stack direction="row" alignItems="center" justifyContent="center" width="100%" minWidth={0} gap={0.5}>
          {icon && (
            <FormIcon
              size={components?.icon?.size ?? "xsmall"}
              color={components?.icon?.color ?? theme.palette.formsComponents.tabs.tabIconColor}
              sx={mergeSx({ position: "relative", zIndex: 1 }, components?.icon?.sx)}
            >
              {icon}
            </FormIcon>
          )}
          <Box
            minWidth={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            sx={mergeSx({ position: "relative", zIndex: 1 }, components?.label?.sx)}
          >
            {label}
          </Box>
          {backgroundContent}
        </Stack>
      );
    };

    const content = <Tab {...rest} sx={styles} ref={ref} disableRipple label={renderLabel()} />;

    if (tooltip) {
      return (
        <FormTooltip title={tooltip}>
          <div>{content}</div>
        </FormTooltip>
      );
    }

    return content;
  }
);

FormTab.displayName = "FormTab";
